/**
 * Redux actions for local content.
 * @module redux/actions/content
 */

import { createAction } from "redux-actions";
import {
  getAssets as apiGetAssets,
  getCategories as apiGetCategories,
  toggleFavorite as apiToggleFavorite,
  getFilterOptions as apiGetFilterOptions
} from "../api/camio";
import AssetSearchUtils from "../util/assetSearchUtils";
import FilterOptions from "../constants/filterOptions";

export const LOAD_MORE_CONTENT = "CONTENT/LOAD_MORE_CONTENT";
export const CLEAR_CONTENT = "CONTENT/CLEAR_CONTENT";
export const ACTIVE_VIEW_CHANGED = "CONTENT/SET_ACTIVE_VIEW";
export const CONTEXT_CHANGED = "CONTENT/CONTEXT_CHANGED";
export const CATEGORY_CHANGED = "CONTENT/CATEGORY_CHANGED";
export const CATEGORIES_CHANGED = "CONTENT/CATEGORIES_CHANGED";
export const FILTER_BY_CHANGED = "CONTENT/FILTER_BY_CHANGED";
export const REPLACEABLE_CATEGORIES_CHANGED =
  "ASSETS/REPLACEABLE_CATEGORIES_CHANGED";
export const SORT_BY_CHANGED = "CONTENT/SORT_BY_CHANGED";
export const SEARCH_TERMS_CHANGED = "CONTENT/SEARCH_TERMS_CHANGED";
export const QUEUE_CONTENT_REQUEST = "CONTENT/QUEUE_CONTENT_REQUEST";
export const SUBMIT_CONTENT_REQUEST = "CONTENT/SUBMIT_CONTENT_REQUEST";
export const CONTENT_REQUEST_FAILED = "CONTENT/CONTENT_REQUEST_FAILED";
export const REQUEST_FILTER_OPTIONS = "CONTENT/REQUEST_FILTER_OPTIONS";
export const RECEIVED_FILTER_OPTIONS = "CONTENT/RECEIVED_FILTER_OPTIONS";
export const FILTER_OPTIONS_REQUEST_FAILED =
  "CONTENT/FILTER_OPTIONS_REQUEST_FAILED";
export const RECEIVED_CONTENT = "CONTENT/RECEIVED_CONTENT";
export const REQUEST_CATEGORIES = "CONTENT/REQUEST_CATEGORIES";
export const RECEIVED_CATEGORIES = "CONTENT/RECEIVED_CATEGORIES";
export const CATEGORIES_REQUEST_FAILED = "CONTENT/CATEGORIES_REQUEST_FAILED";
export const TOGGLE_FAVORITE_SUCCESS = "CONTENT/TOGGLE_FAVORITE_SUCCESS";
export const PAGE_CHANGED = "CONTENT/PAGE_CHANGED";
export const RELOAD = "CONTENT/RELOAD";
export const RESTORE_STATE = "CONTENT/RESTORE_STATE";

export const clearContent = searchType =>
  createAction(CLEAR_CONTENT)({ searchType });
export const changeActiveView = activeView =>
  createAction(ACTIVE_VIEW_CHANGED)({ activeView });
export const changeContext = contextId =>
  createAction(CONTEXT_CHANGED)({ contextId });
export const changeCategory = (groupId, categoryId) =>
  createAction(CATEGORY_CHANGED)({ groupId, categoryId });
const changeCategories = (groupId, categoryIds) =>
  createAction(CATEGORIES_CHANGED)({ groupId, categoryIds });
export const changeReplaceableCategories = replaceableCategories =>
  createAction(REPLACEABLE_CATEGORIES_CHANGED)({ replaceableCategories });
export const changeSortBy = sortBy => createAction(SORT_BY_CHANGED)({ sortBy });
export const changeFilterBy = filterBy =>
  createAction(FILTER_BY_CHANGED)({ filterBy });
export const changePage = (page, searchType) =>
  createAction(PAGE_CHANGED)({ page, searchType });
export const changeSearchTerms = searchTerms =>
  createAction(SEARCH_TERMS_CHANGED)({ searchTerms });
const submitContentRequest = searchType =>
  createAction(SUBMIT_CONTENT_REQUEST)({ searchType });
const queueContentRequest = assetType =>
  createAction(QUEUE_CONTENT_REQUEST)({ assetType });
const handleContentSearchResults = (contentResponse, assetType, searchType) =>
  createAction(RECEIVED_CONTENT)({
    contentList: contentResponse.assets,
    assetType,
    searchType,
    pageNumber: contentResponse.pageInfo.pageIndex,
    totalPages: contentResponse.pageInfo.totalPageCount
  });
const handleContentRequestFailed = searchType =>
  createAction(CONTENT_REQUEST_FAILED)({ searchType });
const requestCategories = createAction(REQUEST_CATEGORIES);
export const handleReceiveCategoryList = categoryList =>
  createAction(RECEIVED_CATEGORIES)({ categoryList });
export const handleFilterOptionsReceived = filterList =>
  createAction(RECEIVED_FILTER_OPTIONS)({ filterList });
const categoriesRequestFailed = createAction(CATEGORIES_REQUEST_FAILED);
const handleToggleFavoriteSuccess = (id, favorite, searchType) =>
  createAction(TOGGLE_FAVORITE_SUCCESS)({ id, favorite, searchType });

export function loadContent(assetType, assetCategory, restrictAssetCategory) {
  return dispatch =>
    Promise.all([
      dispatch(queueContentRequest(assetType)),
      dispatch(clearContent("localcontent")),
      dispatch(getCategories(assetType, assetCategory, restrictAssetCategory))
    ]);
}

export const getFilterOptions = (userId, assetType) => dispatch => {
  dispatch(createAction(REQUEST_FILTER_OPTIONS)());
  apiGetFilterOptions(userId, AssetSearchUtils.translateAssetType(assetType))
    .then(filterList =>
      dispatch(handleFilterOptionsReceived(filterList.options))
    )
    .catch(error => {
      console.log(error);
      dispatch(createAction(FILTER_OPTIONS_REQUEST_FAILED)());
    });
};

export function getAllContent(assetType) {
  return (dispatch, getState) => {
    const { contentList } = getState().content;
    Object.keys(contentList).forEach(key => {
      dispatch(getContent(assetType, contentList[key].searchType));
    });
  };
}

export function getContent(assetType, searchType) {
  return (dispatch, getState) => {
    const currentState = getState().content;
    const currentContent = currentState.contentList[searchType];

    if (currentState.contentLoading) return;

    dispatch(submitContentRequest(searchType));
    const filterByTranslation = AssetSearchUtils.translateFilterBy(
      currentState.filterBy
    );
    const sortByTranslation = AssetSearchUtils.translateSortBy(
      currentState.sortBy,
      currentState.filterBy === FilterOptions.FAVORITE
    );

    const payload = {
      searchType,
      activeView: currentState.activeView,
      assetType,
      categories: currentState.categoryList,
      timeRange: filterByTranslation.timeRange,
      contextId: currentState.contextId,
      pageNumber: currentContent.pageNumber,
      searchTerms: currentState.searchTerms,
      filterBy: filterByTranslation.filterBy,
      sortBy: sortByTranslation.sortBy,
      sortDirection: sortByTranslation.sortDirection,
      maxAssets: currentContent.maxContent
    };

    apiGetAssets(payload)
      .then(contentResponse =>
        dispatch(
          handleContentSearchResults(contentResponse, assetType, searchType)
        )
      )
      .catch(error => {
        console.log(error);
        dispatch(clearContent(searchType));
        return dispatch(handleContentRequestFailed(searchType));
      });
  };
}

export function getCategories(assetType) {
  return dispatch => {
    dispatch(requestCategories());
    apiGetCategories(assetType)
      .then(categoryList => {
        return dispatch(handleReceiveCategoryList(categoryList));
      })
      .catch(error => {
        console.log(error);
        return dispatch(categoriesRequestFailed());
      });
  };
}

export function toggleFavorite(id, favorite, searchType) {
  return dispatch => {
    apiToggleFavorite(id, favorite)
      .then(() =>
        dispatch(handleToggleFavoriteSuccess(id, favorite, searchType))
      )
      .catch(error => {
        console.log(error);
        return Promise.reject();
      });
  };
}

export function reload() {
  return dispatch => {
    dispatch(clearContent("localcontent"));
    dispatch(createAction(RELOAD)({ searchType: "localcontent" }));
    dispatch(clearContent("trackcontent"));
    dispatch(createAction(RELOAD)({ searchType: "trackcontent" }));
  };
}

export const saveState = () => (dispatch, getState) => {
  const currentState = getState().content;
  return {
    contextId: currentState.contextId,
    categoryList: currentState.categoryList,
    filterBy: currentState.filterBy,
    assetType: currentState.assetType,
    activeView: currentState.activeView,
    timeRange: currentState.timeRange
  };
};

export const restoreState = state => (dispatch, getState) =>
  dispatch(createAction(RESTORE_STATE)({ ...state.content }));

export const actions = {
  loadContent,
  getAllContent,
  getContent,
  getCategories,
  changeContext,
  changeCategory,
  changeCategories,
  changeActiveView,
  changeFilterBy,
  changeSortBy,
  changeSearchTerms,
  toggleFavorite,
  changePage,
  reload,
  getFilterOptions,
  changeReplaceableCategories
};



// WEBPACK FOOTER //
// ./app/actions/content.js